import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, X, Loader2, FileLock2, CheckCircle2, AlertTriangle, Building2 } from 'lucide-react';
import { openClinicalDetail, clearClinicalDetail } from '../../redux/mocSlice';
import useSession from '../../hooks/useSession';
import { KIND_MAP, prettyKey, formatScalar, formatDateTime, isBlank, isSystemIdKey } from './mocCommon';
import { getPlatformRoleName } from '../../utils/platformPermissions';

// BREAK-GLASS — reading ONE clinical record out of a hospital the Super Admin
// does not belong to, behind a reason that is written into the audit log.
//
// The whole point of this dialog is that it must NOT feel like an expander. The
// server writes the audit row BEFORE it reads any clinical field (a refused
// attempt is logged too), so by the time content appears the entry already
// exists and cannot be withdrawn. The UI therefore states the consequence in
// full before the reason box, labels the button "Record reason & open record"
// rather than "View", and shows the returned auditId afterwards so the operator
// can see the exact row their name is on.

const MIN_REASON = 10;
const MAX_REASON = 2000;

// Every documented failure code from POST /moc-admin/patients/:id/clinical.
// The server's own message is decent, but these say what it means for the
// operator standing here — in particular that a refusal was still logged.
const ERROR_MESSAGES = {
    reason_too_short: `The reason must be at least ${MIN_REASON} characters. It is stored word for word and is what a reviewer reads back later.`,
    reason_too_long: `The reason must be ${MAX_REASON} characters or fewer so it can be stored verbatim.`,
    bad_kind: 'This timeline row has a record type the platform does not recognise, so there is nothing to open. The attempt was logged.',
    missing_ref: 'This timeline row carries no record reference, so there is nothing to open. The attempt was logged.',
    no_linked_hospitals: 'This identity has no live hospital links, so it holds no clinical records. The attempt was logged.',
    not_this_identity: 'No such record for this MOC identity — nothing was opened. The attempt was logged against your account.',
    audit_write_failed: 'Access refused: the audit entry could not be written, so nothing was read. Break-glass never reads off the record — try again, and raise it if it keeps failing.',
};

// The break-glass payload is NOT a fixed schema — it is shaped per kind and the
// API adds fields over time — so it is rendered structurally rather than mapped
// field by field. A hand-written renderer per kind would quietly hide whatever
// was added last.
const MAX_DEPTH = 4;

const DetailScalar = ({ label, value }) => (
    <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</span>
        <span className="text-sm text-gray-800 break-words">{formatScalar(value)}</span>
    </div>
);

const DetailNode = ({ value, depth = 0 }) => {
    if (depth > MAX_DEPTH) {
        return <pre className="text-[11px] text-gray-600 whitespace-pre-wrap break-words">{JSON.stringify(value)}</pre>;
    }

    if (Array.isArray(value)) {
        return (
            <div className="space-y-2">
                {value.map((entry, i) => (
                    <div key={i} className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                        <DetailNode value={entry} depth={depth + 1} />
                    </div>
                ))}
            </div>
        );
    }

    if (value && typeof value === 'object') {
        const entries = Object.entries(value).filter(([k, v]) => !isBlank(v) && !isSystemIdKey(k));
        if (entries.length === 0) return <span className="text-xs text-gray-400">—</span>;

        const scalars = entries.filter(([, v]) => !v || typeof v !== 'object');
        const nested = entries.filter(([, v]) => v && typeof v === 'object');

        return (
            <div className="space-y-3">
                {scalars.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                        {scalars.map(([k, v]) => <DetailScalar key={k} label={prettyKey(k)} value={v} />)}
                    </div>
                )}
                {nested.map(([k, v]) => (
                    <div key={k}>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-[#2D8A8A] mb-1.5">
                            {prettyKey(k)}{Array.isArray(v) ? ` (${v.length})` : ''}
                        </p>
                        <DetailNode value={v} depth={depth + 1} />
                    </div>
                ))}
            </div>
        );
    }

    return <span className="text-sm text-gray-800 break-words">{formatScalar(value)}</span>;
};

const ConsequenceNotice = ({ viewer, identityLabel, row }) => (
    <div
        data-testid="break-glass-consequence"
        className="rounded-xl border-2 border-[#D7A05C]/50 bg-[#D7A05C]/10 p-4"
    >
        <p className="flex items-center gap-2 text-sm font-bold text-[#8a5f26]">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            This is a logged action, not a preview.
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-[#6b4c1f]">
            The moment you confirm, an entry is written to the tamper-evident audit log —
            <span className="font-semibold"> before</span> a single clinical field is read. The entry is
            append-only: it cannot be edited or removed, and it stays there whether or not the record
            turns out to exist. Refused attempts are recorded too.
        </p>
        <ul className="mt-3 space-y-1 text-[12px] text-[#6b4c1f]">
            <li>
                <span className="font-semibold">Recorded as:</span>{' '}
                {viewer.name || 'your Super Admin account'}
                {viewer.role ? ` · ${viewer.role}` : ''}
            </li>
            <li><span className="font-semibold">Identity opened:</span> {identityLabel}</li>
            <li>
                <span className="font-semibold">Record:</span>{' '}
                {KIND_MAP[row?.kind]?.singular || row?.kind}
                {row?.at ? ` · ${formatDateTime(row.at)}` : ''}
                {row?.hospitalName ? ` · ${row.hospitalName}` : ''}
            </li>
            <li><span className="font-semibold">Your reason:</span> stored word for word, never redacted.</li>
        </ul>
        <p className="mt-3 text-[11px] text-[#8a5f26]">
            Reviewable under Audit &amp; Security → Security Monitoring.
        </p>
    </div>
);

export default function BreakGlassModal({ mocAccountId, patientName, row, onClose }) {
    const { t } = useTranslation('sidebar');
    const dispatch = useDispatch();
    const session = useSession();
    const admin = useSelector((s) => s.admin?.admin);
    const { clinical, clinicalLoading, clinicalError, clinicalErrorCode } = useSelector((s) => s.moc);

    const [reason, setReason] = useState('');

    // Never inherit a previous row's record. Break-glass results are per-audit,
    // so the slate is wiped on open as well as on close — a stale detail on
    // screen would be clinical content with no audit row behind it.
    useEffect(() => {
        dispatch(clearClinicalDetail());
        setReason('');
        return () => { dispatch(clearClinicalDetail()); };
    }, [dispatch, row?.kind, row?.ref]);

    const trimmed = reason.trim();
    const tooShort = trimmed.length < MIN_REASON;
    const tooLong = trimmed.length > MAX_REASON;
    // The server's 400 must never be the first time the operator hears about the
    // length rule — a refused attempt costs them an audit row for nothing.
    const canSubmit = !tooShort && !tooLong && !clinicalLoading && !clinical;

    const viewer = {
        name: admin?.name ? `${admin.name} ${admin.surname || ''}`.trim() : null,
        userId: session.userId,
        // Platform staff are named by their platform ROLE ("Operations Manager"),
        // not the bare 'platform_staff' token, so the audit banner reads well.
        role: session.isSuperAdmin ? 'Super Admin' : (getPlatformRoleName() || session.role),
    };

    const submit = () => {
        if (!canSubmit) return;
        dispatch(openClinicalDetail({ mocAccountId, reason: trimmed, kind: row.kind, ref: row.ref }));
    };

    const KindIcon = KIND_MAP[row?.kind]?.icon || FileLock2;
    // What a person reads for "whose record": the name, else a neutral word.
    // Never the Mitra One Care id (owner rule, 29 Sep 2026).
    const identityLabel = patientName || t('appointmentmodal.patient_fallback', 'Patient');
    const hospitalWord = t('moc.hr_detail_hospital', 'Hospital');

    return (
        <div
            data-testid="break-glass-modal"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-y-auto custom-scrollbar"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header — gold, not teal. The section's own colour would read as
                    "another panel"; this is the one action here with a permanent
                    record attached. */}
                <div className="flex items-start justify-between gap-3 p-5 border-b-2 border-[#D7A05C]/40 bg-[#D7A05C]/5 rounded-t-2xl sticky top-0 z-10">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="p-2.5 bg-[#D7A05C] rounded-xl shadow-sm flex-shrink-0">
                            <ShieldAlert className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-base sm:text-lg font-bold text-gray-800">
                                {t('sidebar.break_glass_title', 'Break-glass clinical access')}
                            </h2>
                            <p className="text-xs text-gray-600 mt-0.5">
                                {identityLabel}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        data-testid="break-glass-close"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-700 cursor-pointer flex-shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Which row this is about, restated — the timeline behind the
                        overlay is not readable while this is open. */}
                    <div className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <KindIcon className="w-4 h-4 text-[#2D8A8A] mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 text-sm">
                            <p className="font-semibold text-gray-800">{row?.title || KIND_MAP[row?.kind]?.singular}</p>
                            {row?.subtitle && <p className="text-xs text-gray-500">{row.subtitle}</p>}
                            <p className="text-[11px] text-gray-400 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                <span className="inline-flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />{row?.hospitalName || hospitalWord}
                                </span>
                                <span>{formatDateTime(row?.at)}</span>
                            </p>
                        </div>
                    </div>

                    {!clinical && (
                        <>
                            <ConsequenceNotice viewer={viewer} identityLabel={identityLabel} row={row} />

                            <div>
                                <label htmlFor="break-glass-reason" className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Why do you need to open this record?
                                </label>
                                <textarea
                                    id="break-glass-reason"
                                    data-testid="break-glass-reason"
                                    rows={4}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="e.g. Patient complaint MOC-4471 — verifying the dispensed quantity against what the hospital billed."
                                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD]"
                                />
                                <div className="flex items-center justify-between mt-1.5 text-[11px]">
                                    <span className={tooShort ? 'text-[#D7A05C] font-semibold' : 'text-gray-400'}>
                                        {tooShort
                                            ? `${MIN_REASON - trimmed.length} more character${MIN_REASON - trimmed.length === 1 ? '' : 's'} needed`
                                            : 'Long enough to be a useful record.'}
                                    </span>
                                    <span
                                        data-testid="break-glass-counter"
                                        className={tooLong ? 'text-red-600 font-semibold' : tooShort ? 'text-gray-400' : 'text-[#2D8A8A] font-semibold'}
                                    >
                                        {trimmed.length} / {MIN_REASON} min · {MAX_REASON} max
                                    </span>
                                </div>
                            </div>
                        </>
                    )}

                    {clinicalError && (
                        <div data-testid="break-glass-error" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
                            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <p className="text-[13px] text-red-700">
                                {ERROR_MESSAGES[clinicalErrorCode] || clinicalError}
                            </p>
                        </div>
                    )}

                    {clinical && (
                        <div data-testid="break-glass-result" className="space-y-4">
                            {/* The audit id is the receipt: it names the exact row
                                the operator's justification was written into. */}
                            <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <div className="text-[13px] text-emerald-800">
                                    <p className="font-semibold">Recorded, then opened.</p>
                                    <p className="mt-0.5">
                                        Audit entry <span className="font-mono font-bold">#{clinical.auditId}</span>
                                        {' · '}{viewer.name || 'your Super Admin account'}
                                        {' · '}{clinical.hospitalName || hospitalWord}
                                        {' · '}{identityLabel}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <p className="text-[11px] font-extrabold uppercase tracking-widest text-[#2D8A8A] mb-2">
                                    {KIND_MAP[clinical.kind]?.singular || clinical.kind}
                                </p>
                                <div className="rounded-xl border border-gray-100 p-4">
                                    <DetailNode value={clinical.detail} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100 bg-white sticky bottom-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer"
                    >
                        {clinical ? 'Close' : 'Cancel'}
                    </button>
                    {!clinical && (
                        <button
                            type="button"
                            data-testid="break-glass-confirm"
                            onClick={submit}
                            disabled={!canSubmit}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#D7A05C] text-white text-sm font-semibold shadow hover:bg-[#c08f4e] transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {clinicalLoading
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <FileLock2 className="w-4 h-4" />}
                            Record reason &amp; open record
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
